import React, { useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  TimeScale
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Scatter } from 'react-chartjs-2';

const conditionBandsPlugin = {
  id: 'conditionBandsPlugin',
  beforeDraw: (chart, args, pluginOptions) => {
    if (!pluginOptions?.showConditionBands || !pluginOptions?.conditionBands?.length) {
      return;
    }

    const { ctx, chartArea, scales } = chart;
    const yScale = scales.y;

    if (!chartArea || !yScale) {
      return;
    }

    ctx.save();

    pluginOptions.conditionBands.forEach((band) => {
      if (typeof band.low !== 'number' || typeof band.high !== 'number') {
        return;
      }

      const top = yScale.getPixelForValue(band.high);
      const bottom = yScale.getPixelForValue(band.low);
      const height = bottom - top;

      ctx.fillStyle = `${band.tone}22`;
      ctx.fillRect(chartArea.left, top, chartArea.right - chartArea.left, height);
    });

    ctx.restore();
  }
};

// Draws three angled bands that follow the regression line on the mileage chart:
// green (core / range), purple (premium, above), yellow (below average, below).
const mileageBandsPlugin = {
  id: 'mileageBandsPlugin',
  beforeDraw: (chart, args, pluginOptions) => {
    const opts = pluginOptions;
    if (!opts?.showMileageBands || !opts?.regression) return;

    const { ctx, chartArea, scales } = chart;
    const xScale = scales.x;
    const yScale = scales.y;
    if (!chartArea || !xScale || !yScale) return;
    if (xScale.type !== 'linear') return; // only on the miles view

    const r = opts.regression;
    const { slope, intercept, standardError, sampleSize, meanX, sxx, zMultiplier, colors } = r;
    if (!colors) return;

    // Sample x across the visible mileage range
    const xMin = xScale.min;
    const xMax = xScale.max;
    if (!Number.isFinite(xMin) || !Number.isFinite(xMax) || xMax <= xMin) return;

    const steps = 60;
    const outerFactor = 0.75; // premium / below average thickness vs core half-width

    const yTop = chartArea.top;
    const yBottom = chartArea.bottom;
    const clampPx = (py) => Math.min(Math.max(py, yTop), yBottom);

    // Cache the polygon sample points across redraws — hover triggers a full
    // chart redraw (and a parent re-render), so without this we recompute
    // 60 prediction-SE points per mouse move.
    const cacheKey = `${slope}|${intercept}|${standardError}|${sampleSize}|${meanX}|${sxx}|${zMultiplier}|${xMin}|${xMax}`;
    let points = chart.$mileageBandsCache && chart.$mileageBandsCache.key === cacheKey
      ? chart.$mileageBandsCache.points
      : null;

    if (!points) {
      points = [];
      for (let i = 0; i <= steps; i++) {
        const x = xMin + ((xMax - xMin) * i) / steps;
        const pred = intercept + slope * x;
        const sePred = standardError * Math.sqrt(
          1 + (1 / sampleSize) + ((x - meanX) ** 2) / (sxx || 1)
        );
        const half = zMultiplier * sePred;
        points.push({
          x,
          pred,
          low: pred - half,
          high: pred + half,
          premiumHigh: pred + half + outerFactor * half * 2,
          belowLow: pred - half - outerFactor * half * 2
        });
      }
      chart.$mileageBandsCache = { key: cacheKey, points };
    }

    const fillPolygon = (color, upperKey, lowerKey) => {
      ctx.beginPath();
      points.forEach((p, idx) => {
        const px = xScale.getPixelForValue(p.x);
        const py = clampPx(yScale.getPixelForValue(p[upperKey]));
        if (idx === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      });
      for (let idx = points.length - 1; idx >= 0; idx--) {
        const p = points[idx];
        const px = xScale.getPixelForValue(p.x);
        const py = clampPx(yScale.getPixelForValue(p[lowerKey]));
        ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fillStyle = `${color}33`;
      ctx.fill();
    };

    ctx.save();
    // Clip to chart area so bands never leak past the plot
    ctx.beginPath();
    ctx.rect(
      chartArea.left,
      chartArea.top,
      chartArea.right - chartArea.left,
      chartArea.bottom - chartArea.top
    );
    ctx.clip();

    fillPolygon(colors.premium, 'premiumHigh', 'high'); // purple above
    fillPolygon(colors.core, 'high', 'low');            // green core
    fillPolygon(colors.below, 'low', 'belowLow');       // yellow below

    ctx.restore();
  }
};

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  TimeScale
);

ChartJS.register(conditionBandsPlugin);
ChartJS.register(mileageBandsPlugin);

const AuctionChart = ({ auctionData, timeframe, allData, onDataPointHover, sortBy, hoveredFilter, showConditionBands, conditionBands, showMileageBands, mileageRegression, xMin, xMax }) => {
  // Track last hovered auction so we only notify the parent when it actually
  // changes — Chart.js fires onHover on every mousemove which would otherwise
  // re-render the entire CarPage on each frame.
  const lastHoveredRef = useRef(undefined);

  // Function to check if a data point matches the hovered filter
  const matchesHoveredFilter = (auction) => {
    if (!hoveredFilter.category || !hoveredFilter.value) return false;
    
    const { category, value } = hoveredFilter;
    if (category === 'soldStatus') {
      return (auction.sold ? 'Sold' : 'Not Sold') === value;
    }
    
    if (category === 'options') {
      return Array.isArray(auction.options) && auction.options.includes(value);
    }
    
    if (category === 'accident') {
      return auction[category]?.toString() === value;
    }
    
    return auction[category]?.toString() === value;
  };

  // Convert auction data to scatter plot format and store original data index
  const soldData = auctionData
    .filter(auction => auction.sold)
    .map((auction, index) => ({
      x: sortBy === 'miles' 
        ? parseFloat(auction.miles?.replace(/[^0-9]/g, '') || '0')
        : auction.auctionDate?.toDate?.() || new Date(auction.auctionDate),
      y: parseFloat(auction.finalBid),
      originalData: auction, // Store the original auction data
      highlighted: matchesHoveredFilter(auction)
    }));

  const unsoldData = auctionData
    .filter(auction => !auction.sold)
    .map((auction, index) => ({
      x: sortBy === 'miles' 
        ? parseFloat(auction.miles?.replace(/[^0-9]/g, '') || '0')
        : auction.auctionDate?.toDate?.() || new Date(auction.auctionDate),
      y: parseFloat(auction.finalBid),
      originalData: auction, // Store the original auction data
      highlighted: matchesHoveredFilter(auction)
    }));

  // Calculate min and max values for x-axis based on all data and sortBy
  let min, max;
  
  if (sortBy === 'miles') {
    // For miles, start at 0 and use max miles values
    const allMiles = allData.map(auction => 
      parseFloat(auction.miles?.replace(/[^0-9]/g, '') || '0')
    );
    min = 0;
    max = Math.max(...allMiles);
    // Add some padding to the max value
    max = Math.ceil(max * 1.1 / 1000) * 1000;
  } else if (xMin && xMax) {
    // Explicit date range provided (e.g. a single calendar month)
    min = xMin;
    max = xMax;
  } else {
    // For dates, use the existing time-based logic
    const now = new Date();
    switch (timeframe) {
      case 'All Time':
        min = new Date(Math.min(
          ...allData.map(auction => 
            auction.auctionDate?.toDate?.() || new Date(auction.auctionDate)
          )
        ));
        min = new Date(min.getFullYear(), min.getMonth(), 1);
        break;
      case '24 Months':
        min = new Date(now.getFullYear(), now.getMonth() - 24, now.getDate());
        break;
      case '18 Months':
        min = new Date(now.getFullYear(), now.getMonth() - 18, now.getDate());
        break;
      case '6 Months':
        min = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
        break;
      case '3 Months':
        min = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
        break;
      case '1 Month':
        min = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        break;
      case '12 Months':
      default:
        min = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
    }
    max = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  }

  const prices = allData.map(auction => parseFloat(auction.finalBid)).filter(p => Number.isFinite(p));
  const maxPrice = prices.length ? Math.max(...prices) : 0;
  const minPrice = prices.length ? Math.min(...prices) : 0;
  
  // Calculate yAxisMax with better rounding based on price range
  let yAxisMax;
  const maxWithPadding = maxPrice * 1.1;
  
  if (maxWithPadding <= 100000) {
    yAxisMax = Math.ceil(maxWithPadding / 10000) * 10000;  // Round to nearest $10k
  } else if (maxWithPadding <= 500000) {
    yAxisMax = Math.ceil(maxWithPadding / 25000) * 25000;  // Round to nearest $25k
  } else if (maxWithPadding <= 1000000) {
    yAxisMax = Math.ceil(maxWithPadding / 50000) * 50000;  // Round to nearest $50k
  } else {
    yAxisMax = Math.ceil(maxWithPadding / 100000) * 100000; // Round to nearest $100k
  }
  
  // Calculate yAxisMin with better rounding based on price range
  let yAxisMin;
  const minWithPadding = minPrice * 0.9;
  
  if (minWithPadding <= 100000) {
    yAxisMin = Math.floor(minWithPadding / 10000) * 10000;  // Round to nearest $10k
  } else if (minWithPadding <= 500000) {
    yAxisMin = Math.floor(minWithPadding / 25000) * 25000;  // Round to nearest $25k
  } else if (minWithPadding <= 1000000) {
    yAxisMin = Math.floor(minWithPadding / 50000) * 50000;  // Round to nearest $50k
  } else {
    yAxisMin = Math.floor(minWithPadding / 100000) * 100000; // Round to nearest $100k
  }
  
  // Ensure minimum is at least 0
  yAxisMin = Math.max(0, yAxisMin);

  // Avoid a degenerate axis when there is no data (e.g. all filters unchecked)
  if (yAxisMax <= yAxisMin) {
    yAxisMax = yAxisMin + 10000;
  }

  const data = {
    datasets: [
      {
        label: 'Sold',
        data: soldData,
        backgroundColor: soldData.map(point => point.highlighted ? '#32C5FF' : '#32C5FF'),
        borderColor: soldData.map(point => point.highlighted ? '#AAAAAA' : '#32C5FF'),
        borderWidth: soldData.map(point => point.highlighted ? 2 : 1),
        pointStyle: 'circle',
        pointRadius: soldData.map(point => point.highlighted ? 8 : 6),
      },
      {
        label: 'Unsold',
        data: unsoldData,
        backgroundColor: unsoldData.map(point => point.highlighted ? '#FFF' : '#FFF'),
        borderColor: unsoldData.map(point => point.highlighted ? '#AAAAAA' : '#32C5FF'),
        borderWidth: unsoldData.map(point => point.highlighted ? 2 : 1),
        pointStyle: 'circle',
        pointRadius: unsoldData.map(point => point.highlighted ? 8 : 6),
      }
    ]
  };

  const handleClick = (event, elements) => {
    if (elements && elements.length > 0) {
      const datasetIndex = elements[0].datasetIndex;
      const index = elements[0].index;
      const dataset = data.datasets[datasetIndex];
      const clickedData = dataset.data[index].originalData;
      
      if (clickedData.auctionLink) {
        window.open(clickedData.auctionLink, '_blank');
      }
    }
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 0
    },
    hover: {
      animationDuration: 0
    },
    responsiveAnimationDuration: 0,
    onClick: handleClick,
    onHover: (event, elements) => {
      if (elements && elements.length > 0) {
        const datasetIndex = elements[0].datasetIndex;
        const index = elements[0].index;
        const dataset = data.datasets[datasetIndex];
        const hoveredData = dataset.data[index].originalData;
        if (lastHoveredRef.current !== hoveredData) {
          lastHoveredRef.current = hoveredData;
          onDataPointHover(hoveredData);
        }
        if (event?.native?.target) {
          event.native.target.style.cursor = hoveredData.auctionLink ? 'pointer' : 'default';
        }
      } else {
        if (lastHoveredRef.current !== null) {
          lastHoveredRef.current = null;
          onDataPointHover(null);
        }
        if (event?.native?.target) {
          event.native.target.style.cursor = 'default';
        }
      }
    },
    scales: {
      x: sortBy === 'miles' ? {
        type: 'linear',
        min: min,
        max: max,
        ticks: {
          callback: function(value) {
            return value.toLocaleString() + ' mi';
          },
          color: '#666',
          maxTicksLimit: 10,
          stepSize: (() => {
            const range = max - min;
            if (range <= 5000) return 500;         // 500 mi intervals for very low mileage
            else if (range <= 10000) return 1000;  // 1k intervals for ranges up to 10k
            else if (range <= 25000) return 2500;  // 2.5k intervals for ranges up to 25k
            else if (range <= 50000) return 5000;  // 5k intervals for ranges up to 50k
            else if (range <= 100000) return 10000;
            else if (range <= 200000) return 15000;
            else if (range <= 500000) return 25000;
            else return 50000;
          })()
        },
        afterBuildTicks: function(axis) {
          const range = max - min;
          let stepSize;
          if (range <= 5000) stepSize = 500;
          else if (range <= 10000) stepSize = 1000;
          else if (range <= 25000) stepSize = 2500;
          else if (range <= 50000) stepSize = 5000;
          else if (range <= 100000) stepSize = 10000;
          else if (range <= 200000) stepSize = 15000;
          else if (range <= 500000) stepSize = 25000;
          else stepSize = 50000;
          
          axis.ticks = [];
          for (let i = 0; i <= max; i += stepSize) {
            axis.ticks.push({ value: i });
          }
        },
        grid: {
          display: true,
          color: '#f0f0f0'
        }
      } : {
        type: 'time',
        time: {
          unit: (xMin && xMax && (max - min) / 86400000 <= 62) ? 'day' : 'month',
          displayFormats: {
            month: 'MMM yyyy',
            day: 'MMM d'
          }
        },
        min: min,
        max: max,
        ticks: {
          source: 'auto',
          autoSkip: true,
          maxRotation: 45,
          color: '#666'
        },
        grid: {
          display: true,
          color: '#f0f0f0'
        }
      },
      y: {
        min: yAxisMin,
        max: yAxisMax,
        ticks: {
          callback: function(value) {
            return '$' + value.toLocaleString();
          },
          color: '#666'
        },
        afterBuildTicks: function(axis) {
          let stepSize;
          if (yAxisMax <= 100000) stepSize = 10000;       // $10k intervals up to $100k
          else if (yAxisMax <= 500000) stepSize = 25000;  // $25k intervals up to $500k
          else if (yAxisMax <= 1000000) stepSize = 50000; // $50k intervals up to $1M
          else if (yAxisMax <= 2000000) stepSize = 100000; // $100k intervals up to $2M
          else stepSize = 200000;                          // $200k intervals for higher values
          
          axis.ticks = [];
          for (let i = yAxisMin; i <= yAxisMax; i += stepSize) {
            axis.ticks.push({ value: i });
          }
        },
        grid: {
          display: true,
          color: '#f0f0f0'
        }
      }
    },
    plugins: {
      conditionBandsPlugin: {
        showConditionBands,
        conditionBands
      },
      mileageBandsPlugin: {
        showMileageBands: !!(showMileageBands && sortBy === 'miles' && mileageRegression),
        regression: mileageRegression
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const point = context.raw;
            const dataset = context.dataset;
            const status = dataset.label;
            const date = sortBy === 'miles' 
              ? new Date(point.originalData.auctionDate?.toDate?.() || point.originalData.auctionDate).toLocaleDateString()
              : new Date(point.x).toLocaleDateString();
            const miles = point.originalData.miles;
            const externalLinkIcon = point.originalData.auctionLink ? ' ↗' : '';
            return `${status}: $${point.y.toLocaleString()} (${miles}, ${date})${externalLinkIcon}`;
          }
        }
      }
    }
  };

  return (
    <div style={{ width: '100%', height: '400px' }}>
      <Scatter data={data} options={options} />
    </div>
  );
};

export default AuctionChart;
